import { AuthMode } from "auth/types";
import { getAuthMode, getAuthTokenRefreshInterval } from "config/config";
import { decodeToken, isExpired } from "react-jwt";

import { ensureMsalInitialized, msalInstance, msalScopes } from "./msalConfig";
import { AuthTokenDecoded } from "./types";

const TOKEN_KEY = "epms_auth_token";

export const setAuthToken = (token: string) => {
    window.localStorage.setItem(TOKEN_KEY, token);
};

export const getAuthToken = (): string | null => {
    return window.localStorage.getItem(TOKEN_KEY);
};

export const clearAuthToken = () => {
    window.localStorage.removeItem(TOKEN_KEY);
};

export const getAuthTokenDecoded = (): AuthTokenDecoded | undefined => {
    const token = getAuthToken();
    return token ? (decodeToken(token) as AuthTokenDecoded) : undefined;
};

export const isAuthTokenExpired = (token: string) => {
    return isExpired(token);
};

let lastRefreshTime = 0;

export const refreshAuthToken = async () => {
    const authMode = getAuthMode();
    if (authMode === AuthMode.NONE) {
        // Nothing to do further
        return;
    }

    const currentTime = Date.now();
    if (currentTime - lastRefreshTime < getAuthTokenRefreshInterval()) {
        // Token refresh skipped - too soon
        return;
    }

    const token = await fetchAuthToken();
    lastRefreshTime = currentTime;
    setAuthToken(token!);
};

export const fetchAuthToken = async (): Promise<string | null> => {
    const authMode = getAuthMode();

    switch (authMode) {
        case AuthMode.MS_ENTRA: {
            await ensureMsalInitialized();

            const accounts = msalInstance.getAllAccounts();
            if (accounts.length === 0) {
                throw new Error("No account found!");
            }

            const request = {
                scopes: msalScopes,
                account: accounts[0],
            };

            const response = await msalInstance.acquireTokenSilent(request);

            return response.accessToken;
        }

        case AuthMode.NONE: {
            // Indicate no token in this mode
            return null;
        }

        case AuthMode.BASIC: {
            // TODO: Implement refresh token for auth mode BASIC
            return getAuthToken();
        }

        default:
            throw new Error(`Unknown auth mode: ${authMode}`);
    }
};
